import * as React from 'react';
// mui
import {
  Button,
  Divider,
  DialogContent,
  TextField,
  DialogActions,
  Slider,
  Grid,
  Dialog,
  DialogTitle,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  DatePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
// icons
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import FlagIcon from '@mui/icons-material/Flag';
// redux
import { useSelector } from 'react-redux';
import { selectTaskById } from '../../../store/selectors/taskSelectors';
import { selectAllProjects, selectSelectedProjectId } from '../../../store/selectors/projectSelectors';
import { selectRecurringTaskById } from '../../../store/selectors/recuringTaskSelectors';
// dayjs
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import 'dayjs/locale/en-gb';
// types
import RecurringTask, { Interval } from '../../../interfaces/RecurringTask.interface';
import Task from '../../../interfaces/Task.interface';
import Project from '../../../interfaces/Project.interface';
// axios
import api from '../../../axios';
// components
import { desktopSliderMarks, mobileSliderMarks } from '../../common/sliderMarks';
import ItemBreadcrumbs from '../../common/ItemBreadcrumbs';

const emptyRecurringTaskData: RecurringTask = {
  id: 0,
  title: '',
  description: '',
  energy: 4,
  time: 2,
  priority: 1,
  startDate: '',
  interval: 'daily',
  parentTaskId: null,
  projectId: null,
  createdDate: null,
  updatedDate: null,
  permanentlyCompleted: false,
  excused: false,
  excuseExpiresAt: null,
  completed: false,
  dueDate: '',
  selectedDays: []
}

interface RecurringTaskDialogProps {
  onSave: () => void;
  onCancel: () => void;
  addMode: Boolean;
  parentTaskId: number | null;
  recurringTaskId: number | null; // can be null if dialog in add mode
}

export default function RecurringTaskDialog(props: RecurringTaskDialogProps) {
  const { onSave, onCancel, addMode, parentTaskId, recurringTaskId } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // redux
  const selectParentTask = parentTaskId
    ? selectTaskById(parentTaskId)
    : () => null;
  const parentTaskRaw = useSelector(selectParentTask);
  const parentTaskData: Task | null = parentTaskRaw ?? null;
  
  const selectRecurringTasks: any = recurringTaskId
    ? selectRecurringTaskById(recurringTaskId)
    : () => null;
  const recurringTaskRaw: RecurringTask | undefined = useSelector(selectRecurringTasks);
  const originalRecurringTaskData: RecurringTask = recurringTaskRaw ?? emptyRecurringTaskData;

  const localProjects: Project[] = useSelector(selectAllProjects());
  const localSelectedProjectId: number | null = useSelector(selectSelectedProjectId());

  let projectIdToUse: number | null = 0;
  if (parentTaskData) { // first try parent project
    projectIdToUse = parentTaskData.projectId;
  } else if (addMode) { // second try selected project
    projectIdToUse = localSelectedProjectId;
  } else { // thirdly try existing project (generally the case for parentless tasks and will default null)
    projectIdToUse = originalRecurringTaskData.projectId;
  }
  const [ recurringTaskData, setRecurringTaskData ] = React.useState<RecurringTask>({
    ...originalRecurringTaskData,
    parentTaskId: parentTaskId,
    projectId: projectIdToUse === null ? 0 : projectIdToUse // shift null to 0 for use in select component
  });
  const [ open, setOpen ] = React.useState(true); // by default open the modal as it is conditionally rendered in parent
  const [ loading, setLoading ] = React.useState(false);

  const handleSelectDay = (
    event: React.MouseEvent<HTMLElement>,
    newDays: string[],
  ) => {
    setRecurringTaskData({
      ...recurringTaskData,
      selectedDays: newDays
    });
  };

  const handleDialogClose = () => {
    onCancel();
    setOpen(false);
  }

  const handleDialogSave = () => {
    setLoading(true);
    
    let saveData: any = {...recurringTaskData};

    if (saveData.interval !== 'daily') {
      delete saveData.selectedDays;
    }

    // local components use 0 for null, replace this before posting to API
    if (saveData.projectId === 0) {
      saveData.projectId = null;
    }

    if (addMode) {
      if (typeof parentTaskId === 'number') {
        saveData = {
          ...saveData,
          parentTaskId: parentTaskId
        };
      }

      api.post(`/recurring/create`, saveData)
        .then((res) => {
          console.log(res);

          setOpen(false);
          setLoading(false);

          onSave();
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
        });
    } else {
      api.put(`/recurring/update/${saveData.id}`, saveData)
        .then((res) => {
          console.log(res);

          setOpen(false);
          setLoading(false);

          onSave();
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
        });
    }
  }
  
  return (
    <Dialog
      open={open}
      fullScreen={isMobile}
      onClose={handleDialogClose}
      fullWidth
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
          },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, pt: 1 }}>
        <ItemBreadcrumbs
          currentItem={recurringTaskData}
          itemType="recurring"
          includeCurrentItem={false}
        />
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: isMobile ? 2 : '' }}>
        <Grid sx={{ mb: 2 }} container spacing={1}>
          <Grid size={12} >
            <TextField
              value={recurringTaskData.title}
              onChange={(e) => { setRecurringTaskData({
                ...recurringTaskData,
                title: e.target.value
              }); }}
              sx={{ mt: 0 }}
              autoFocus
              required
              margin="dense"
              id="name"
              label="Title"
              fullWidth
              variant="standard"
            />
          </Grid>
        </Grid>

        <Grid sx={{ mb: 2 }} container spacing={1}>
          <Grid size={isMobile ? 6 : 4} >
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Project</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={recurringTaskData.projectId}
                label="Project"
                disabled={parentTaskId !== null}
                onChange={(e) => {
                  setRecurringTaskData({
                    ...recurringTaskData,
                    projectId: Number(e.target.value)
                  });
                }}
              >
                { localProjects.map((project: Project) => (
                  <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={isMobile ? 6 : 4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="YYYY/MM/DD"
                value={recurringTaskData.startDate ? dayjs.utc(recurringTaskData.startDate).local() : null}
                onChange={(val) => {
                  if (val && val.isValid()) {
                    setRecurringTaskData({
                      ...recurringTaskData,
                      startDate: val.utc().toISOString(),
                    });
                  } else {
                    setRecurringTaskData({
                      ...recurringTaskData,
                      startDate: '',
                    });
                  }
                }}
                label="Start Date"
              />
            </LocalizationProvider>
          </Grid>
          <Grid size={isMobile ? 12 : 4} sx={{ pt: isMobile ? 1 : 0 }}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Interval</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={recurringTaskData.interval}
                label="Interval"
                onChange={(e) => {
                  setRecurringTaskData({
                    ...recurringTaskData,
                    interval: e.target.value as Interval
                  });
                }}
              >
                <MenuItem value={'daily'}>Daily</MenuItem>
                <MenuItem value={'weekly'}>Weekly</MenuItem>
                <MenuItem value={'fortnightly'}>Fortnightly</MenuItem>
                <MenuItem value={'monthly'}>Monthly</MenuItem>
                <MenuItem value={'yearly'}>Yearly</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        { recurringTaskData.interval === 'daily' &&
          <ToggleButtonGroup
            value={recurringTaskData.selectedDays}
            onChange={handleSelectDay}
            fullWidth
            color="primary"
            size={"small"}
            sx={{ mb: 2, mt: 0 }}
          >
            <ToggleButton value="sunday">Sun</ToggleButton>
            <ToggleButton value="monday">Mon</ToggleButton>
            <ToggleButton value="tuesday">Tue</ToggleButton>
            <ToggleButton value="wednesday">Wed</ToggleButton>
            <ToggleButton value="thursday">Thu</ToggleButton>
            <ToggleButton value="friday">Fri</ToggleButton>
            <ToggleButton value="saturday">Sat</ToggleButton>
          </ToggleButtonGroup>
        }
        
        <TextField
          label="Description"
          value={recurringTaskData.description}
          onChange={(e) => {
            setRecurringTaskData({
              ...recurringTaskData,
              description: e.target.value
            });
          }}
          sx={{ paddingBottom: 0 }}
          multiline
          rows={4}
          fullWidth
        />
        <Grid container spacing={1} sx={{ paddingX: isMobile ? 2 : 3, pl: 0, paddingY: 1, width: '100%' }}>
          <Grid size={1.5}>
            <ElectricBoltIcon sx={{ mt: isMobile ? 1.5 : 0.5 }} />
          </Grid>
          <Grid size={10.5}>
            <Slider
              aria-label="Energy"
              value={recurringTaskData.energy}
              onChange={(e: any) => {
                setRecurringTaskData({
                  ...recurringTaskData,
                  energy: e.target.value
                });
              }}
              defaultValue={1}
              min={1}
              max={5}
              step={1}
              valueLabelDisplay="auto"
              marks={isMobile ? mobileSliderMarks.energy : desktopSliderMarks.energy}
            />
          </Grid>
        </Grid>
        <Grid container spacing={1} sx={{ paddingX: isMobile ? 2 : 3, pl: 0, paddingY: 1, width: '100%' }}>
          <Grid size={1.5}>
            <FlagIcon sx={{ mt: isMobile ? 1.5 : 0.5 }} />
          </Grid>
          <Grid size={10.5}>
            <Slider
              aria-label="Priority"
              value={recurringTaskData.priority}
              onChange={(e: any) => {
                setRecurringTaskData({
                  ...recurringTaskData,
                  priority: e.target.value
                });
              }}
              defaultValue={1}
              min={1}
              max={5}
              step={1}
              valueLabelDisplay="auto"
              marks={isMobile ? mobileSliderMarks.priority : desktopSliderMarks.priority}
            />
          </Grid>
        </Grid>
        <Grid container spacing={1} sx={{ paddingX: isMobile ? 2 : 3, pl: 0, paddingY: 1, width: '100%' }}>
          <Grid size={1.5}>
            <AccessAlarmIcon sx={{ mt: isMobile ? 1.5 : 0.5 }} />
          </Grid>
          <Grid size={10.5}>
            <Slider
              aria-label="Time"
              value={recurringTaskData.time}
              onChange={(e: any) => {
                setRecurringTaskData({
                  ...recurringTaskData,
                  time: e.target.value
                });
              }}
              defaultValue={2}
              min={1}
              max={5}
              step={1}
              valueLabelDisplay="auto"
              marks={isMobile ? mobileSliderMarks.time : desktopSliderMarks.time}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleDialogClose}>Cancel</Button>
        <Button
          variant="contained"
          loading={loading}
          onClick={(e) => { e.preventDefault(); handleDialogSave() }}
          disabled={
            !recurringTaskData.title
          || (recurringTaskData.startDate === '' && !dayjs(recurringTaskData.startDate).isValid())
          || (recurringTaskData.interval === 'daily' ? !recurringTaskData.selectedDays?.length : false)}
        >{addMode ? 'Create' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  )
}